import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Separator } from './ui/separator';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Home,
  Calendar,
  Loader2,
  Building2,
  Users,
  BarChart3,
  Trophy,
  Percent,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { motion } from 'motion/react';
import { dealAPI } from '../lib/deal-api';
import { adminUserAPI } from '../lib/admin-api';
import { API_BASE, getAuthHeaders, apiFetch } from '../lib/api-fetch';
import { Deal } from '../types/deal';
import { User } from '../types/admin';

// ── Types ──────────────────────────────────────────────────────────────────────

interface AgentPerformance {
  id: string;
  name: string;
  role: string;
  commissionRate: number;
  totalListingValue: number;
  potentialCommission: number;
  closedDeals: number;
  closedValue: number;
  earnedCommission: number;
  activeDeals: number;
  totalDeals: number;
  closeRate: number;
  avgDealSize: number;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const CHART_COLORS = [
  '#FDB022', '#10b981', '#3b82f6', '#8b5cf6', '#ef4444',
  '#f59e0b', '#06b6d4', '#ec4899', '#14b8a6', '#f97316',
];

const MEDAL_COLORS: Record<number, string> = {
  0: 'text-yellow-400',
  1: 'text-gray-300',
  2: 'text-amber-600',
};

const TOOLTIP_STYLE = {
  backgroundColor: '#1a1a1a',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: '#fff',
};

// ── Component ──────────────────────────────────────────────────────────────────

// Analytics Lambda response types
interface AnalyticsListing {
  id: string;
  title: string;
  status: string;
  propertyType: string;
  price: number;
  location: string;
  agentId?: string;
}

interface AnalyticsAgent {
  agentId: string;
  name: string;
  email: string;
  deals: { total: number; closed: number; pending: number; lost: number };
  revenue: { total: number; commission: number; averagePerDeal: number };
  listings: { active: number; sold: number; total: number };
  performance: { dealConversionRate: number };
}

interface RevenueEntry {
  date: string;
  revenue: number;
  sources?: { propertySales: number; commissions: number; fees: number };
}

export function AdminAnalytics() {
  // Analytics Lambda data (verified server-side sources — F-010 fix)
  const [analyticsListings, setAnalyticsListings] = useState<AnalyticsListing[]>([]);
  const [analyticsAgents, setAnalyticsAgents] = useState<AnalyticsAgent[]>([]);
  const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueEntry[]>([]);
  // Supplementary data — documented gaps (backend frozen, commissionRate + stage data not in analytics Lambdas)
  const [deals, setDeals] = useState<Deal[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      // Analytics Lambdas (primary data sources — F-010: all 3 called here)
      const [agentPerfRaw, propListingsRaw, revenueRaw, dealsData, usersData] = await Promise.all([
        apiFetch(`${API_BASE}/admin/analytics/agent-performance?period=quarter`, { headers: getAuthHeaders() })
          .then(r => r.json()).then(d => d.data?.agents ?? []).catch(() => []),
        apiFetch(`${API_BASE}/admin/analytics/property-listings`, { headers: getAuthHeaders() })
          .then(r => r.json()).then(d => d.data?.listings ?? []).catch(() => []),
        apiFetch(`${API_BASE}/admin/analytics/revenue`, { headers: getAuthHeaders() })
          .then(r => r.json()).then(d => d.data?.breakdown ?? []).catch(() => []),
        // Supplementary: deals for stage distribution donut (no analytics Lambda for per-stage counts)
        dealAPI.getDeals().catch(() => []),
        // Supplementary: users for commissionRate per agent (not available in analytics Lambda — backend frozen)
        adminUserAPI.getUsers().catch(() => []),
      ]);
      setAnalyticsAgents(agentPerfRaw);
      setAnalyticsListings(propListingsRaw);
      setRevenueBreakdown(revenueRaw);
      setDeals(dealsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading analytics data', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ── Trend computation from analytics revenue Lambda + deals (deal counts only) ─
  // Revenue data sourced from GET /admin/analytics/revenue (F-010 fix)
  // Deal counts retained from dealAPI — analytics Lambda has no time-series deal counts (backend frozen)

  const { currentMonth, lastMonth, lastYearSameMonth, trends, monthlyTrends } = useMemo(() => {
    const now = new Date();
    const curYear = now.getFullYear();
    const curMon = now.getMonth();

    // Build monthly revenue map from analytics Lambda breakdown
    const revenueByMonth: Record<string, number> = {};
    revenueBreakdown.forEach(entry => {
      if (!entry.date) return;
      const d = new Date(entry.date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      revenueByMonth[key] = (revenueByMonth[key] || 0) + (entry.revenue || 0);
    });

    const getMonthRevenue = (year: number, mon: number) => revenueByMonth[`${year}-${mon}`] || 0;

    // Deal counts still from dealAPI (documented gap — no time-series in analytics Lambda)
    const inMonth = (ts: any, year: number, mon: number) => {
      if (!ts) return false;
      const d = new Date(typeof ts === 'number' ? ts : ts);
      return d.getFullYear() === year && d.getMonth() === mon;
    };

    const lastMonIdx = curMon === 0 ? 11 : curMon - 1;
    const lastMonYear = curMon === 0 ? curYear - 1 : curYear;

    const dealsThisMonth = deals.filter(d => inMonth(d.createdDate, curYear, curMon));
    const dealsLastMonth = deals.filter(d => inMonth(d.createdDate, lastMonYear, lastMonIdx));
    const dealsLastYear = deals.filter(d => inMonth(d.createdDate, curYear - 1, curMon));

    const closedCount = (arr: Deal[]) => arr.filter(d => d.stage === 'closed').length;

    const curRevenue = getMonthRevenue(curYear, curMon);
    const lastRevenue = getMonthRevenue(lastMonYear, lastMonIdx);
    const lastYearRevenue = getMonthRevenue(curYear - 1, curMon);

    const curM = { deals: dealsThisMonth.length, closedDeals: closedCount(dealsThisMonth), revenue: curRevenue };
    const lastM = { deals: dealsLastMonth.length, closedDeals: closedCount(dealsLastMonth), revenue: lastRevenue };
    const lastY = { deals: dealsLastYear.length, closedDeals: closedCount(dealsLastYear), revenue: lastYearRevenue };

    const pct = (cur: number, prev: number) => prev === 0 ? 0 : Math.round(((cur - prev) / prev) * 1000) / 10;

    const trendCalc = {
      mom: { dealsChange: pct(curM.deals, lastM.deals), revenueChange: pct(curM.revenue, lastM.revenue) },
      yoy: { dealsChange: pct(curM.deals, lastY.deals), revenueChange: pct(curM.revenue, lastY.revenue) },
    };

    // Build last 12 months — revenue from Lambda, deal counts from dealAPI
    const monthly = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(curYear, curMon - 11 + i, 1);
      const y = d.getFullYear();
      const m = d.getMonth();
      const monthDeals = deals.filter(deal => inMonth(deal.createdDate, y, m));
      return {
        month: d.toLocaleString('default', { month: 'short', year: '2-digit' }),
        deals: monthDeals.length,
        closedDeals: closedCount(monthDeals),
        revenue: getMonthRevenue(y, m),  // analytics Lambda revenue
      };
    });

    return { currentMonth: curM, lastMonth: lastM, lastYearSameMonth: lastY, trends: trendCalc, monthlyTrends: monthly };
  }, [revenueBreakdown, deals]);

  const getTrendIcon = (change: number) => {
    if (change > 0) return <TrendingUp className="w-4 h-4" />;
    if (change < 0) return <TrendingDown className="w-4 h-4" />;
    return null;
  };

  // ── Currency formatters ──────────────────────────────────────────────────────

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `R${(value / 1000000000).toFixed(2)}B`;
    if (value >= 1000000) return `R${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `R${(value / 1000).toFixed(0)}k`;
    return `R${value.toLocaleString()}`;
  };

  const formatFullCurrency = (value: number) =>
    `R ${value.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  // ── Portfolio computed values ────────────────────────────────────────────────

  // Portfolio values — sourced from GET /admin/analytics/property-listings (F-010 fix)
  const totalPortfolioValue = useMemo(
    () => analyticsListings.reduce((sum, p) => sum + (p.price || 0), 0),
    [analyticsListings],
  );

  const publishedListingValue = useMemo(
    () => analyticsListings.filter(p => ['active', 'published'].includes((p.status || '').toLowerCase())).reduce((sum, p) => sum + (p.price || 0), 0),
    [analyticsListings],
  );

  // ── Agent performance — sourced from GET /admin/analytics/agent-performance (F-010 fix) ──
  // Supplementary: commissionRate from users (not in analytics Lambda — backend frozen)
  // Supplementary: totalListingValue from analyticsListings (property-listings Lambda)

  const agentPerformance = useMemo((): AgentPerformance[] => {
    // Build listing value map per agentId from property-listings Lambda data
    const agentListingValueMap = new Map<string, number>();
    analyticsListings.forEach(l => {
      if (l.agentId) {
        agentListingValueMap.set(l.agentId, (agentListingValueMap.get(l.agentId) || 0) + (l.price || 0));
      }
    });

    return analyticsAgents
      .map(agent => {
        // commissionRate from users — supplementary (not in analytics Lambda)
        const user = users.find(u => u.id === agent.agentId || u.email === agent.email);
        const rate = user?.commissionRate || 3;
        const role = user?.role || 'Agent';

        const totalListingValue = agentListingValueMap.get(agent.agentId) || 0;
        const potentialCommission = totalListingValue * (rate / 100);

        const closedDeals = agent.deals?.closed ?? 0;
        const lostDeals = agent.deals?.lost ?? 0;
        const totalDeals = agent.deals?.total ?? 0;
        const activeDeals = Math.max(0, totalDeals - closedDeals - lostDeals);

        return {
          id: agent.agentId,
          name: agent.name,
          role,
          commissionRate: rate,
          totalListingValue,
          potentialCommission,
          closedDeals,
          closedValue: agent.revenue?.total ?? 0,
          earnedCommission: agent.revenue?.commission ?? 0,
          activeDeals,
          totalDeals,
          closeRate: agent.performance?.dealConversionRate ?? 0,
          avgDealSize: agent.revenue?.averagePerDeal ?? 0,
        };
      })
      .sort((a, b) => b.closedValue - a.closedValue);
  }, [analyticsAgents, analyticsListings, users]);

  const commissionChartData = useMemo(
    () =>
      agentPerformance
        .filter(a => a.totalListingValue > 0 || a.earnedCommission > 0)
        .map(a => ({
          name: (a.name || '').split(' ')[0],
          fullName: a.name,
          potentialCommission: Math.round(a.potentialCommission),
          earnedCommission: Math.round(a.earnedCommission),
          rate: a.commissionRate,
        })),
    [agentPerformance],
  );

  const topPerformers = useMemo(
    () =>
      [...agentPerformance]
        .filter(a => a.closedDeals > 0 || a.totalDeals > 0)
        .sort((a, b) => (b.closedValue !== a.closedValue ? b.closedValue - a.closedValue : b.closedDeals - a.closedDeals)),
    [agentPerformance],
  );

  const dealStageData = useMemo(() => {
    const map = new Map<string, number>();
    deals.forEach(d => map.set(d.stage, (map.get(d.stage) || 0) + 1));
    const labels: Record<string, string> = {
      lead: 'Lead',
      'viewing-scheduled': 'Viewing',
      'offer-made': 'Offer Made',
      'offer-accepted': 'Accepted',
      financing: 'Financing',
      inspection: 'Inspection',
      closing: 'Closing',
      closed: 'Closed',
      lost: 'Lost',
    };
    return Array.from(map.entries()).map(([stage, count]) => ({
      name: labels[stage] || stage,
      value: count,
    }));
  }, [deals]);

  const totalPotentialCommission = agentPerformance.reduce((s, a) => s + a.potentialCommission, 0);
  // Closed sales value and commissions — sourced from agent-performance Lambda (F-010 fix)
  const totalClosedValue = useMemo(
    () => agentPerformance.reduce((s, a) => s + a.closedValue, 0),
    [agentPerformance],
  );
  const totalCommissionsEarned = useMemo(
    () => agentPerformance.reduce((s, a) => s + a.earnedCommission, 0),
    [agentPerformance],
  );

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#FDB022] mx-auto" />
          <p className="text-muted-foreground text-sm">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-10">
      {/* ════════════════════════════════════════════════════════════════════════
          SECTION 1 – Portfolio Value & Commissions
          ════════════════════════════════════════════════════════════════════════ */}
      <section className="space-y-6">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#FDB022]" />
            Portfolio Value & Commissions
          </h3>
          <p className="text-muted-foreground mt-1">
            Total listing values, potential commissions, and closed sales
          </p>
        </div>

        {/* Key metric cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
            <Card className="relative overflow-hidden border-[#FDB022]/20 bg-gradient-to-br from-[#FDB022]/5 to-transparent h-full" data-testid="analytics-admin-total-portfolio-card">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#FDB022]/5 rounded-bl-full" />
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground" data-testid="analytics-admin-total-portfolio-label">Total Portfolio Value</CardTitle>
                <DollarSign className="h-5 w-5 text-[#FDB022]" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-[#FDB022]" data-testid="analytics-admin-total-portfolio-value">{formatCurrency(totalPortfolioValue)}</div>
                <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-admin-total-portfolio-count">
                  {analyticsListings.length} listing{analyticsListings.length !== 1 ? 's' : ''} total
                </p>
                <p className="text-xs text-muted-foreground" data-testid="analytics-admin-published-value">{formatCurrency(publishedListingValue)} published</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="relative overflow-hidden h-full" data-testid="analytics-admin-potential-commissions-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground" data-testid="analytics-admin-potential-commissions-label">Potential Commissions</CardTitle>
                <Percent className="h-5 w-5 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-400" data-testid="analytics-admin-potential-commissions-value">{formatCurrency(totalPotentialCommission)}</div>
                <p className="text-xs text-muted-foreground mt-1">Based on individual agent rates</p>
                <p className="text-xs text-muted-foreground" data-testid="analytics-admin-agents-with-listings-count">
                  {agentPerformance.filter(a => a.totalListingValue > 0).length} agents with listings
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="relative overflow-hidden h-full" data-testid="analytics-admin-closed-sales-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground" data-testid="analytics-admin-closed-sales-label">Closed Sales Value</CardTitle>
                <TrendingUp className="h-5 w-5 text-green-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-400" data-testid="analytics-admin-closed-sales-value">{formatCurrency(totalClosedValue)}</div>
                <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-admin-closed-deals-count">
                  {agentPerformance.reduce((s, a) => s + a.closedDeals, 0)} deal
                  {agentPerformance.reduce((s, a) => s + a.closedDeals, 0) !== 1 ? 's' : ''} closed
                </p>
                <p className="text-xs text-green-400/70" data-testid="analytics-admin-commissions-earned-value">{formatCurrency(totalCommissionsEarned)} commission earned</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="relative overflow-hidden h-full" data-testid="analytics-admin-active-pipeline-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground" data-testid="analytics-admin-active-pipeline-label">Active Pipeline</CardTitle>
                <BarChart3 className="h-5 w-5 text-blue-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-400" data-testid="analytics-admin-active-pipeline-count">
                  {agentPerformance.reduce((s, a) => s + a.activeDeals, 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Active deals in pipeline</p>
                <p className="text-xs text-muted-foreground" data-testid="analytics-admin-active-pipeline-value">
                  {formatCurrency(
                    deals
                      .filter(d => d.stage !== 'closed' && d.stage !== 'lost')
                      .reduce((s, d) => s + (d.offerAmount || 0), 0),
                  )}{' '}
                  pipeline value
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Commissions bar chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <Card className="border-white/5" data-testid="analytics-admin-commissions-chart-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#FDB022]" />
                Potential Commissions by Agent
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Based on each agent's assigned listings and individual commission rate
              </p>
            </CardHeader>
            <CardContent data-testid="analytics-admin-commissions-chart-container">
              {commissionChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={380}>
                  <BarChart data={commissionChartData} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" tick={{ fill: '#b5b5b5', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                    <YAxis tick={{ fill: '#b5b5b5', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} tickFormatter={v => formatCurrency(v)} />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value: number, name: string) => [
                        formatFullCurrency(value),
                        name === 'potentialCommission' ? 'Potential Commission' : 'Earned Commission',
                      ]}
                      labelFormatter={(label: string, payload: any[]) => {
                        const d = payload?.[0]?.payload;
                        return d ? `${d.fullName} (${d.rate}% rate)` : label;
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#b5b5b5' }} formatter={v => (v === 'potentialCommission' ? 'Potential Commission' : 'Earned Commission')} />
                    <Bar dataKey="potentialCommission" fill="#FDB022" radius={[4, 4, 0, 0]} maxBarSize={50} />
                    <Bar dataKey="earnedCommission" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={50} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
                  <p>No agent listing data available yet</p>
                  <p className="text-xs mt-1">Assign properties to agents to see commission projections</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </section>

      <Separator className="opacity-20" />

      {/* ════════════════════════════════════════════════════════════════════════
          SECTION 2 – Top Performers & Pipeline
          ════════════════════════════════════════════════════════════════════════ */}
      <section className="space-y-6">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Trophy className="w-6 h-6 text-[#FDB022]" />
            Agent Performance & Deal Pipeline
          </h3>
          <p className="text-muted-foreground mt-1">
            Top performing agents ranked by closed sales and current pipeline distribution
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top performers leaderboard */}
          <motion.div className="lg:col-span-2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            <Card className="border-white/5 h-full" data-testid="analytics-admin-top-performers-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#FDB022]" />
                  Top Performing Agents
                </CardTitle>
                <p className="text-sm text-muted-foreground">Ranked by closed sales value from deal pipelines</p>
              </CardHeader>
              <CardContent data-testid="analytics-admin-top-performers-container">
                {topPerformers.length > 0 ? (
                  <div className="space-y-3">
                    {topPerformers.map((agent, index) => (
                      <motion.div
                        key={agent.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + index * 0.08 }}
                        className={`flex items-center gap-4 p-4 rounded-xl border transition-all hover:border-[#FDB022]/30 ${
                          index === 0
                            ? 'bg-gradient-to-r from-[#FDB022]/10 to-transparent border-[#FDB022]/20'
                            : 'bg-white/[0.02] border-white/5'
                        }`}
                      >
                        <div className="flex-shrink-0 w-8 text-center">
                          {index < 3 ? (
                            <Trophy className={`w-6 h-6 mx-auto ${MEDAL_COLORS[index]}`} />
                          ) : (
                            <span className="text-lg font-bold text-muted-foreground">#{index + 1}</span>
                          )}
                        </div>
                        <Avatar className="h-10 w-10 flex-shrink-0">
                          <AvatarFallback
                            className={`text-sm font-bold ${
                              index === 0 ? 'bg-[#FDB022]/20 text-[#FDB022]' : 'bg-white/10 text-white'
                            }`}
                          >
                            {agent.name
                              .split(' ')
                              .map(n => n[0])
                              .join('')
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold truncate">{agent.name}</span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                              {agent.commissionRate}%
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span>{agent.closedDeals} closed</span>
                            <span className="text-white/20">|</span>
                            <span>{agent.activeDeals} active</span>
                            <span className="text-white/20">|</span>
                            <span>{agent.closeRate.toFixed(0)}% close rate</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-bold text-green-400">{formatCurrency(agent.closedValue)}</div>
                          <div className="text-xs text-muted-foreground">{formatCurrency(agent.earnedCommission)} earned</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <Trophy className="w-12 h-12 mb-3 opacity-30" />
                    <p>No deal pipeline data available</p>
                    <p className="text-xs mt-1">Deals will appear here as agents close sales</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Pipeline donut */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <Card className="border-white/5 h-full" data-testid="analytics-admin-deal-pipeline-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                  Deal Pipeline
                </CardTitle>
                <p className="text-sm text-muted-foreground">Distribution across stages</p>
              </CardHeader>
              <CardContent data-testid="analytics-admin-deal-pipeline-chart-container">
                {dealStageData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={dealStageData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" paddingAngle={3} stroke="none">
                          {dealStageData.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v} deal${v !== 1 ? 's' : ''}`, 'Count']} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2 mt-2">
                      {dealStageData.map((stage, i) => (
                        <div key={stage.name} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                            <span className="text-muted-foreground">{stage.name}</span>
                          </div>
                          <span className="font-medium">{stage.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
                    <p>No deals in pipeline</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Agent commission breakdown table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
          <Card className="border-white/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FDB022]" />
                Agent Commission Breakdown
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Detailed view of each agent's listing values, commission rates, and potential earnings
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground">Agent</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Rate</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Listing Value</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Potential Commission</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Closed Deals</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Closed Value</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Earned</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground">Close Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentPerformance.map((agent, index) => (
                      <tr key={agent.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {index < 3 && agent.closedValue > 0 && <Trophy className={`w-4 h-4 flex-shrink-0 ${MEDAL_COLORS[index]}`} />}
                            <span className="font-medium">{agent.name}</span>
                          </div>
                        </td>
                        <td className="text-right py-3 px-4">
                          <Badge variant="outline" className="text-xs">
                            {agent.commissionRate}%
                          </Badge>
                        </td>
                        <td className="text-right py-3 px-4 text-[#FDB022]">{agent.totalListingValue > 0 ? formatCurrency(agent.totalListingValue) : '-'}</td>
                        <td className="text-right py-3 px-4 text-purple-400">{agent.potentialCommission > 0 ? formatCurrency(agent.potentialCommission) : '-'}</td>
                        <td className="text-right py-3 px-4">
                          {agent.closedDeals > 0 ? <span className="text-green-400 font-medium">{agent.closedDeals}</span> : <span className="text-muted-foreground">0</span>}
                        </td>
                        <td className="text-right py-3 px-4">
                          {agent.closedValue > 0 ? <span className="text-green-400">{formatCurrency(agent.closedValue)}</span> : <span className="text-muted-foreground">-</span>}
                        </td>
                        <td className="text-right py-3 px-4">
                          {agent.earnedCommission > 0 ? (
                            <span className="text-green-400 font-medium">{formatCurrency(agent.earnedCommission)}</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="text-right py-3 px-4">
                          {agent.totalDeals > 0 ? (
                            <span className={agent.closeRate >= 50 ? 'text-green-400' : 'text-muted-foreground'}>{agent.closeRate.toFixed(0)}%</span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {agentPerformance.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-muted-foreground">
                          No agent data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {agentPerformance.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-[#FDB022]/20 font-bold">
                        <td className="py-3 px-4 text-[#FDB022]">TOTALS</td>
                        <td className="text-right py-3 px-4">-</td>
                        <td className="text-right py-3 px-4 text-[#FDB022]">{formatCurrency(agentPerformance.reduce((s, a) => s + a.totalListingValue, 0))}</td>
                        <td className="text-right py-3 px-4 text-purple-400">{formatCurrency(totalPotentialCommission)}</td>
                        <td className="text-right py-3 px-4 text-green-400">{agentPerformance.reduce((s, a) => s + a.closedDeals, 0)}</td>
                        <td className="text-right py-3 px-4 text-green-400">{formatCurrency(agentPerformance.reduce((s, a) => s + a.closedValue, 0))}</td>
                        <td className="text-right py-3 px-4 text-green-400">{formatCurrency(agentPerformance.reduce((s, a) => s + a.earnedCommission, 0))}</td>
                        <td className="text-right py-3 px-4">-</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </section>

      <Separator className="opacity-20" />

      {/* ════════════════════════════════════════════════════════════════════════
          SECTION 3 – Trends & Historical Data
          ════════════════════════════════════════════════════════════════════════ */}
      <section className="space-y-6">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#FDB022]" />
            Analytics & Trends
          </h3>
          <p className="text-muted-foreground mt-1">Year-over-Year and Month-over-Month performance metrics</p>
        </div>

        {/* Current month performance */}
        <div>
          <h4 className="mb-4 flex items-center gap-2 text-base font-semibold">
            <Calendar className="w-5 h-5" />
            Current Month Performance
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
                <Home className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{currentMonth?.deals || 0}</div>
                <p className="text-xs text-muted-foreground mt-1">{currentMonth?.closedDeals || 0} closed</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatCurrency(currentMonth?.revenue || 0)}</div>
                <p className="text-xs text-muted-foreground mt-1">Commission earned</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Close Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {currentMonth?.deals > 0 ? ((currentMonth.closedDeals / currentMonth.deals) * 100).toFixed(1) : 0}%
                </div>
                <p className="text-xs text-muted-foreground mt-1">Deals closed</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* MoM + YoY side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* MoM */}
          <div>
            <h4 className="mb-4 text-base font-semibold">Month-over-Month (MoM)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Deals Change</span>
                    <Badge variant={trends?.mom?.dealsChange >= 0 ? 'default' : 'destructive'} className="flex items-center gap-1">
                      {getTrendIcon(trends?.mom?.dealsChange || 0)}
                      {Math.abs(trends?.mom?.dealsChange || 0).toFixed(1)}%
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Current: {currentMonth?.deals || 0} deals</p>
                    <p>Last Month: {lastMonth?.deals || 0} deals</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Revenue Change</span>
                    <Badge variant={trends?.mom?.revenueChange >= 0 ? 'default' : 'destructive'} className="flex items-center gap-1">
                      {getTrendIcon(trends?.mom?.revenueChange || 0)}
                      {Math.abs(trends?.mom?.revenueChange || 0).toFixed(1)}%
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Current: {formatCurrency(currentMonth?.revenue || 0)}</p>
                    <p>Last Month: {formatCurrency(lastMonth?.revenue || 0)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* YoY */}
          <div>
            <h4 className="mb-4 text-base font-semibold">Year-over-Year (YoY)</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Deals Change</span>
                    <Badge variant={trends?.yoy?.dealsChange >= 0 ? 'default' : 'destructive'} className="flex items-center gap-1">
                      {getTrendIcon(trends?.yoy?.dealsChange || 0)}
                      {Math.abs(trends?.yoy?.dealsChange || 0).toFixed(1)}%
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>This Year: {currentMonth?.deals || 0} deals</p>
                    <p>Last Year: {lastYearSameMonth?.deals || 0} deals</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Revenue Change</span>
                    <Badge variant={trends?.yoy?.revenueChange >= 0 ? 'default' : 'destructive'} className="flex items-center gap-1">
                      {getTrendIcon(trends?.yoy?.revenueChange || 0)}
                      {Math.abs(trends?.yoy?.revenueChange || 0).toFixed(1)}%
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>This Year: {formatCurrency(currentMonth?.revenue || 0)}</p>
                    <p>Last Year: {formatCurrency(lastYearSameMonth?.revenue || 0)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* 12-Month Trend */}
        <Card>
          <CardHeader>
            <CardTitle>12-Month Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={monthlyTrends || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="month" tick={{ fill: '#b5b5b5', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                <YAxis yAxisId="left" tick={{ fill: '#b5b5b5', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: '#b5b5b5', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.1)' }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(value: any) => formatCurrency(value)} />
                <Legend wrapperStyle={{ color: '#b5b5b5' }} />
                <Line yAxisId="left" type="monotone" dataKey="deals" stroke="#FDB022" strokeWidth={2} name="Total Deals" />
                <Line yAxisId="left" type="monotone" dataKey="closedDeals" stroke="#10b981" strokeWidth={2} name="Closed Deals" />
                <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Deal Volume (R)" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Monthly Breakdown Table */}
        <Card>
          <CardHeader>
            <CardTitle>Monthly Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2 px-4">Month</th>
                    <th className="text-right py-2 px-4">Deals</th>
                    <th className="text-right py-2 px-4">Closed</th>
                    <th className="text-right py-2 px-4">Deal Volume</th>
                    <th className="text-right py-2 px-4">Close Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyTrends?.map((month: any, index: number) => (
                    <tr key={index} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                      <td className="py-2 px-4">{month.month}</td>
                      <td className="text-right py-2 px-4">{month.deals}</td>
                      <td className="text-right py-2 px-4">{month.closedDeals}</td>
                      <td className="text-right py-2 px-4">{formatCurrency(month.revenue)}</td>
                      <td className="text-right py-2 px-4">
                        {month.deals > 0 ? ((month.closedDeals / month.deals) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
